
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useState, useEffect } from "react";
import { useToast } from "@/components/ui/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface CustomerDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (customer: Customer) => void;
  initialData?: Customer;
}

interface Customer {
  id?: string;
  name: string;
  phone: string;
  whatsapp?: string;
  description?: string;
}

export function CustomerDialog({ isOpen, onOpenChange, onSave, initialData }: CustomerDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<{[key: string]: string}>({});
  const [customer, setCustomer] = useState<Customer>({
    name: "",
    phone: "",
    whatsapp: "",
    description: "",
  });
  const { toast } = useToast();

  useEffect(() => {
    if (initialData) {
      setCustomer(initialData);
    } else {
      setCustomer({
        name: "",
        phone: "",
        whatsapp: "",
        description: "",
      });
    }
    // Clear any previous errors when dialog opens/closes
    setErrors({});
  }, [initialData, isOpen]);

  // Check if the phone number already exists
  const checkPhoneExists = async (phone: string): Promise<boolean> => {
    if (!phone) return false;
    if (initialData && initialData.phone === phone) return false; // Skip check if updating with same phone

    const { data, error } = await supabase
      .from("customers")
      .select("phone")
      .eq("phone", phone)
      .maybeSingle();

    return !!data;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setErrors({});

    try {
      // Validate required fields
      const newErrors: {[key: string]: string} = {};
      if (!customer.name) newErrors.name = "Nome é obrigatório";
      if (!customer.phone) newErrors.phone = "Telefone é obrigatório";
      
      // Check for duplicate phone
      const phoneExists = await checkPhoneExists(customer.phone);
      if (phoneExists) {
        newErrors.phone = "Este telefone já está cadastrado para outro cliente";
      }
      
      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        setIsLoading(false);
        return;
      }

      console.log("Saving customer:", customer);

      const customerData = {
        name: customer.name,
        phone: customer.phone,
        whatsapp: customer.whatsapp || null,
        description: customer.description || null,
      };

      let result;
      if (initialData) {
        // Update existing customer
        result = await supabase
          .from("customers")
          .update(customerData)
          .eq('id', initialData.id);
      } else {
        // Insert new customer
        result = await supabase
          .from("customers")
          .insert([customerData]);
      }

      const { error } = result;

      if (error) {
        console.error("Error saving customer:", error);
        
        // Handle specific error for duplicate phone
        if (error.code === "23505" && error.message.includes("customers_phone_key")) {
          setErrors({ phone: "Este telefone já está cadastrado para outro cliente" });
          toast({
            title: "Erro ao salvar cliente",
            description: "Este telefone já está cadastrado para outro cliente",
            variant: "destructive",
          });
        } else {
          toast({
            title: "Erro ao salvar cliente",
            description: error.message,
            variant: "destructive",
          });
        }
        return;
      }

      toast({
        title: initialData ? "Cliente atualizado" : "Cliente cadastrado",
        description: `${customer.name} foi ${initialData ? "atualizado" : "cadastrado"} com sucesso!`,
      });

      onSave(customer);
      onOpenChange(false);
    } catch (error) {
      console.error("Unexpected error:", error);
      toast({
        title: "Erro inesperado",
        description: "Ocorreu um erro ao salvar o cliente. Tente novamente.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px] bg-white">
        <DialogHeader>
          <DialogTitle className="text-[#004080]">
            {initialData ? "Editar Cliente" : "Cadastrar Novo Cliente"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name">Nome *</Label>
            <Input
              id="name"
              value={customer.name}
              onChange={(e) => setCustomer({ ...customer, name: e.target.value })}
              placeholder="Nome do cliente"
              required
              className={`border-gray-300 focus:border-[#0073B7] focus:ring-[#0073B7] ${errors.name ? 'border-red-500' : ''}`}
            />
            {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="phone">Telefone *</Label>
            <Input
              id="phone"
              value={customer.phone}
              onChange={(e) => setCustomer({ ...customer, phone: e.target.value })}
              placeholder="(00) 00000-0000"
              required
              className={`border-gray-300 focus:border-[#0073B7] focus:ring-[#0073B7] ${errors.phone ? 'border-red-500' : ''}`}
            />
            {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone}</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="whatsapp">WhatsApp</Label>
            <Input
              id="whatsapp"
              value={customer.whatsapp}
              onChange={(e) => setCustomer({ ...customer, whatsapp: e.target.value })}
              placeholder="(00) 00000-0000"
              className="border-gray-300 focus:border-[#0073B7] focus:ring-[#0073B7]"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="description">Descrição</Label>
            <Textarea
              id="description"
              value={customer.description}
              onChange={(e) => setCustomer({ ...customer, description: e.target.value })}
              placeholder="Informações adicionais sobre o cliente"
              className="border-gray-300 focus:border-[#0073B7] focus:ring-[#0073B7]"
            />
          </div>
          <Button 
            type="submit" 
            className="w-full bg-[#0073B7] hover:bg-[#004080] text-white"
            disabled={isLoading}
          >
            {isLoading ? (
              "Salvando..."
            ) : (
              initialData ? "Salvar Alterações" : "Salvar Cliente"
            )}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
